import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useToast } from '@/lib/hooks/useToast'
import { MicrosoftIcon } from '@/components/icons/MicrosoftIcon'
import { API_BASE_URL } from '@/lib/services/api'

interface MicrosoftSignInButtonProps {
  /**
   * Optional redirect path after successful login
   * If not provided, defaults to root (/) which will be handled by CrisisGuard
   */
  redirectPath?: string
  /**
   * Button variant (default: outline)
   */
  variant?: 'default' | 'outline' | 'gradient' | 'ghost' | 'link'
  /**
   * Full width button
   */
  fullWidth?: boolean
  /**
   * Custom className
   */
  className?: string
}

/**
 * Microsoft Sign-In Button
 *
 * Initiates Azure AD OAuth flow by redirecting to the backend /api/v1/auth/azure/login endpoint.
 * The backend will:
 * 1. Generate an authorization URL with state (CSRF protection)
 * 2. Redirect user to Microsoft login page
 * 3. Handle the callback after user authenticates
 * 4. Validate tenant whitelist and create/link user
 * 5. Issue Crisis Buddy JWT and redirect back to frontend
 *
 * Usage:
 * ```tsx
 * <MicrosoftSignInButton />
 * ```
 */
export function MicrosoftSignInButton({
  redirectPath,
  variant = 'outline',
  fullWidth = true,
  className,
}: MicrosoftSignInButtonProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)

  const handleMicrosoftSignIn = () => {
    setIsLoading(true)

    try {
      // Build the redirect URL for after login (frontend origin)
      const currentUrl = window.location.origin
      const afterLoginUrl = redirectPath
        ? `${currentUrl}${redirectPath}`
        : `${currentUrl}/` // CrisisGuard will route to dashboard or crisis select

      // Construct the backend Azure login URL
      // Use API_BASE_URL if available, otherwise construct from current origin
      // but pointing to the API port (5000 instead of 5173)
      let apiBase = API_BASE_URL
      if (!apiBase || apiBase === '') {
        // If API_BASE_URL is not configured, try to construct it dynamically
        const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
        if (isDev) {
          // In development, API is usually on port 5000
          apiBase = `http://${window.location.hostname}:5000`
        } else {
          // In production, assume API is on the same origin
          apiBase = window.location.origin
        }
      }

      const azureLoginUrl = new URL('/api/v1/auth/azure/login', apiBase)
      azureLoginUrl.searchParams.set('redirect_uri', afterLoginUrl)

      // Redirect to backend Azure login endpoint
      window.location.href = azureLoginUrl.toString()
    } catch (error) {
      console.error('[MicrosoftSignInButton] Error initiating Microsoft sign-in:', error)
      setIsLoading(false)
      toast({
        title: t('auth.microsoftSignInError', 'Microsoft Sign-In Error'),
        description: t(
          'auth.microsoftSignInErrorDescription',
          'Failed to initiate Microsoft sign-in. Please try again.'
        ),
        variant: 'destructive',
      })
    }
  }

  return (
    <Button
      type="button"
      variant={variant}
      className={`${fullWidth ? 'w-full' : ''} h-12 text-base font-semibold ${className || ''}`}
      onClick={handleMicrosoftSignIn}
      disabled={isLoading}
    >
      <MicrosoftIcon className="mr-2 h-5 w-5" />
      {isLoading
        ? t('auth.signingIn', 'Signing in...')
        : t('auth.signInWithMicrosoft', 'Sign in with Microsoft')}
    </Button>
  )
}
